import { useNavigate } from 'react-router-dom'
import {
    RiUploadCloud2Line,
    RiBankCard2Line,
    RiHome4Line,
    RiPrinterLine,
    RiArrowRightLine,
    RiMoneyDollarCircleLine,
    RiCustomerService2Line,
    RiTruckLine,
    RiShieldCheckLine,
    RiCheckDoubleLine,
    RiFlashlightLine,
    RiChat3Line,
    RiStarFill,
} from 'react-icons/ri'

import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'
import { Button } from '@/components/ui'

import styles from './Home.module.css'

const steps = [
    {
        number: '01',
        icon: RiUploadCloud2Line,
        title: 'Sube tu archivo',
        description: 'PDF, Word, imagen o diseña desde cero en nuestro editor online. Cualquier formato funciona.',
    },
    {
        number: '02',
        icon: RiBankCard2Line,
        title: 'Elige y paga',
        description: 'Selecciona material, tamaño y cantidad. Paga seguro con tarjeta o Yape vía Izipay.',
    },
    {
        number: '03',
        icon: RiHome4Line,
        title: 'Recibe en casa',
        description: 'Imprimimos con calidad industrial y lo enviamos a cualquier departamento del Perú.',
    },
]

const valueProps = [
    {
        icon: RiPrinterLine,
        title: 'Impresoras industriales',
        description: 'Trabajamos con las mejores imprentas del Perú. Calidad offset y digital de primer nivel.',
    },
    {
        icon: RiMoneyDollarCircleLine,
        title: 'Los mejores precios',
        description: 'Directo de fábrica, sin intermediarios. Precios por millar que no encontrarás en otro lado.',
    },
    {
        icon: RiCustomerService2Line,
        title: 'Soporte 24/7',
        description: 'Chat de soporte gratuito. Te ayudamos con tu diseño, tu pedido o lo que necesites.',
    },
    {
        icon: RiTruckLine,
        title: 'Envío a todo el Perú',
        description: 'Lima, provincias, donde sea. Seguimiento en tiempo real hasta que llegue a tu puerta.',
    },
]

const trustPoints = [
    { icon: RiShieldCheckLine, text: 'Pago 100% seguro' },
    { icon: RiCheckDoubleLine, text: 'Garantía de calidad' },
    { icon: RiFlashlightLine, text: 'Entrega express disponible' },
    { icon: RiChat3Line, text: 'Soporte humano 24/7' },
]

export function Home() {
    const navigate = useNavigate()
    const { isLogged } = useAuthStore()

    const handleCta = () => navigate(isLogged ? ROUTES.projects : ROUTES.register)

    return (
        <div className={styles.page}>
            {/* ── HERO ── */}
            <section className={styles.hero}>
                <div className={styles.heroDecor} aria-hidden="true">
                    <div className={styles.decorOrb1} />
                    <div className={styles.decorOrb2} />
                    <div className={styles.decorGrid} />
                    <div className={styles.decorDots} />
                </div>

                <div className={styles.heroInner}>
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>
                            <span className={styles.badgeDot} />
                            Imprenta online
                        </span>

                        <h1 className={styles.headline}>
                            Imprime tus documentos
                            <br />
                            <span className={styles.headlineAccent}>en minutos.</span>
                        </h1>

                        <p className={styles.heroSub}>
                            Diseña o sube tus volantes, afiches y cualquier documento.
                            Calidad industrial, los mejores precios del Perú, envíos a todo el país.
                        </p>

                        <div className={styles.heroCtas}>
                            <Button variant="primary" size="lg" onClick={handleCta}>
                                {isLogged ? 'Ir a mis proyectos' : 'Empezar gratis'}
                                <RiArrowRightLine />
                            </Button>
                            <span className={styles.ctaHint}>Diseña gratis, paga solo al pedir</span>
                        </div>

                        <div className={styles.heroStats}>
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>24/7</span>
                                <span className={styles.statLabel}>Soporte gratis</span>
                            </div>
                            <div className={styles.statDivider} />
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>25</span>
                                <span className={styles.statLabel}>Departamentos</span>
                            </div>
                            <div className={styles.statDivider} />
                            <div className={styles.stat}>
                                <span className={styles.statNumber}>
                                    <RiStarFill className={styles.statIcon} />
                                </span>
                                <span className={styles.statLabel}>Mejores precios</span>
                            </div>
                        </div>
                    </div>

                    {/* Stacked print-material mockups */}
                    <div className={styles.heroVisual} aria-hidden="true">
                        <div className={`${styles.card} ${styles.cardBack}`}>
                            <div className={styles.cardBackStripe} />
                            <div className={styles.cardBackLines}>
                                <span style={{ width: '55%' }} />
                                <span style={{ width: '40%' }} />
                            </div>
                        </div>

                        <div className={`${styles.card} ${styles.cardMid}`}>
                            <div className={styles.cardMidHeader} />
                            <div className={styles.cardMidBody}>
                                <div className={styles.cardMidImg} />
                                <div className={styles.cardMidText}>
                                    <span style={{ width: '90%' }} />
                                    <span style={{ width: '70%' }} />
                                    <span style={{ width: '80%' }} />
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.card} ${styles.cardFront}`}>
                            <div className={styles.cardFrontTop}>
                                <div className={styles.cardFrontLogo} />
                                <div className={styles.cardFrontTag} />
                            </div>
                            <div className={styles.cardFrontName} />
                            <div className={styles.cardFrontRole} />
                            <div className={styles.cardFrontContact}>
                                <span style={{ width: '45%' }} />
                                <span style={{ width: '55%' }} />
                            </div>
                            <div className={styles.cardFrontAccent} />
                        </div>

                        <div className={styles.floatingBadge}>
                            <span className={styles.floatingBadgeIcon}>
                                <RiPrinterLine />
                            </span>
                            <span>Listo para imprimir</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ── */}
            <section className={styles.process}>
                <div className={styles.sectionInner}>
                    <header className={styles.sectionHeader}>
                        <span className={styles.sectionLabel}>Cómo funciona</span>
                        <h2 className={styles.sectionTitle}>
                            Tres pasos. Así de simple.
                        </h2>
                        <p className={styles.sectionSub}>
                            No necesitas experiencia en diseño ni ir a una imprenta.
                            Todo es online, rápido y sin complicaciones.
                        </p>
                    </header>

                    <div className={styles.stepsGrid}>
                        {steps.map((s, i) => (
                            <div key={s.number} className={styles.stepWrapper}>
                                <div className={styles.step}>
                                    <div className={styles.stepTopRow}>
                                        <div className={styles.stepIconWrap}>
                                            <s.icon />
                                        </div>
                                        <span className={styles.stepNumber}>{s.number}</span>
                                    </div>
                                    <h3 className={styles.stepTitle}>{s.title}</h3>
                                    <p className={styles.stepDesc}>{s.description}</p>
                                </div>
                                {i < steps.length - 1 && (
                                    <div className={styles.stepArrow} aria-hidden="true">
                                        <RiArrowRightLine />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── VALUE PROPS ── */}
            <section className={styles.features}>
                <div className={styles.sectionInner}>
                    <header className={styles.sectionHeader}>
                        <span className={styles.sectionLabel}>Por qué Imprime Digital</span>
                        <h2 className={styles.sectionTitle}>
                            Conectamos tu diseño con las mejores
                            <br />
                            imprentas industriales del Perú
                        </h2>
                        <p className={styles.sectionSub}>
                            No somos una imprenta. Somos tu puente directo a la impresión
                            industrial de calidad, sin intermediarios y al mejor precio.
                        </p>
                    </header>

                    <div className={styles.featureGrid}>
                        {valueProps.map((f) => (
                            <div key={f.title} className={styles.featureCard}>
                                <div className={styles.featureIconWrap}>
                                    <f.icon />
                                </div>
                                <h3 className={styles.featureTitle}>{f.title}</h3>
                                <p className={styles.featureDesc}>{f.description}</p>
                                <div className={styles.featureCardAccent} />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TRUST BAR ── */}
            <section className={styles.trust}>
                <div className={styles.trustInner}>
                    {trustPoints.map((tp) => (
                        <div key={tp.text} className={styles.trustItem}>
                            <tp.icon className={styles.trustIcon} />
                            <span>{tp.text}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── QUALITY SECTION ── */}
            <section className={styles.quality}>
                <div className={styles.qualityInner}>
                    <div className={styles.qualityContent}>
                        <span className={styles.sectionLabel}>Calidad garantizada</span>
                        <h2 className={styles.qualityTitle}>
                            Impresión industrial,
                            <br />
                            <span className={styles.qualityTitleAccent}>no impresora de escritorio.</span>
                        </h2>
                        <p className={styles.qualityDesc}>
                            Cada pedido es procesado por imprentas industriales especializadas
                            en producción a escala. Máquinas offset y digitales de última
                            generación que garantizan color preciso, acabados impecables y
                            materiales premium.
                        </p>
                        <ul className={styles.qualityList}>
                            <li>
                                <RiCheckDoubleLine />
                                Couché, bond, texturado, adhesivo y más
                            </li>
                            <li>
                                <RiCheckDoubleLine />
                                Color CMYK calibrado profesionalmente
                            </li>
                            <li>
                                <RiCheckDoubleLine />
                                Control de calidad antes del envío
                            </li>
                        </ul>
                    </div>
                    <div className={styles.qualityVisual} aria-hidden="true">
                        <div className={styles.qualityCard}>
                            <div className={styles.qualityCardHeader}>
                                <RiPrinterLine />
                                <span>Producción activa</span>
                            </div>
                            <div className={styles.qualityCardRows}>
                                <div className={styles.qualityRow}>
                                    <span className={styles.qualityRowLabel}>Resolución</span>
                                    <span className={styles.qualityRowValue}>300 DPI+</span>
                                </div>
                                <div className={styles.qualityRow}>
                                    <span className={styles.qualityRowLabel}>Color</span>
                                    <span className={styles.qualityRowValue}>CMYK Pro</span>
                                </div>
                                <div className={styles.qualityRow}>
                                    <span className={styles.qualityRowLabel}>Materiales</span>
                                    <span className={styles.qualityRowValue}>12+ tipos</span>
                                </div>
                                <div className={styles.qualityRow}>
                                    <span className={styles.qualityRowLabel}>Acabados</span>
                                    <span className={styles.qualityRowValue}>Mate / Brillo</span>
                                </div>
                            </div>
                            <div className={styles.qualityCardBar}>
                                <div className={styles.qualityCardBarFill} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className={styles.cta}>
                <div className={styles.ctaDecor} aria-hidden="true">
                    <div className={styles.ctaOrb} />
                </div>
                <div className={styles.ctaInner}>
                    <span className={styles.ctaBadge}>Empieza hoy</span>
                    <h2 className={styles.ctaTitle}>
                        Tu próximo impreso
                        <br />
                        <span className={styles.ctaTitleAccent}>está a un clic.</span>
                    </h2>
                    <p className={styles.ctaSub}>
                        Crea tu cuenta, sube tu archivo y cotiza gratis.
                        Solo pagas cuando confirmas tu pedido.
                    </p>
                    <Button variant="primary" size="lg" onClick={handleCta}>
                        {isLogged ? 'Ir a mis proyectos' : 'Crear cuenta gratis'}
                        <RiArrowRightLine />
                    </Button>
                </div>
            </section>
        </div>
    )
}
